import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { useGameStore } from "../../app/gameStore";
import { PALETTE } from "../palette";
import { gridToWorld, TILE_SIZE } from "./gridToWorld";
import { soldBackEdges } from "./moveGhost";

const scratch = new THREE.Object3D();

/**
 * While a machine move is pending, pulses a warning disc on every tile of each
 * pipe the move will sell back (spec 0067 OQ4: pipes do not follow a moved
 * machine). One instanced draw for the lot, like the contamination overlay.
 */
export function SoldBackMarks() {
  const pendingMove = useGameStore((s) => s.pendingMove);
  const snapshot = useGameStore((s) => s.snapshot);
  const meshRef = useRef<THREE.InstancedMesh>(null);

  const tiles = useMemo(() => {
    const out: [number, number][] = [];
    for (const edge of soldBackEdges(snapshot, pendingMove)) {
      for (const pos of edge.path) {
        const [x, , z] = gridToWorld(pos);
        out.push([x, z]);
      }
    }
    return out;
  }, [snapshot, pendingMove]);

  useFrame(({ clock }) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const pulse = 1 + Math.sin(clock.elapsedTime * 6) * 0.18;
    for (let i = 0; i < tiles.length; i++) {
      const [x, z] = tiles[i]!;
      scratch.position.set(x, 0.07, z);
      scratch.rotation.set(-Math.PI / 2, 0, 0);
      scratch.scale.setScalar(pulse);
      scratch.updateMatrix();
      mesh.setMatrixAt(i, scratch.matrix);
    }
    mesh.instanceMatrix.needsUpdate = true;
  });

  // Count is fixed at construction, so a changed set remounts the mesh; r3f
  // disposes the old geometry and material on unmount.
  const count = tiles.length;

  if (count === 0) return null;

  return (
    <instancedMesh key={count} ref={meshRef} args={[undefined, undefined, count]} renderOrder={902} frustumCulled={false}>
      <circleGeometry args={[TILE_SIZE * 0.22, 14]} />
      <meshBasicMaterial
        color={PALETTE.hazardWarning}
        transparent
        opacity={0.9}
        depthWrite={false}
        depthTest={false}
      />
    </instancedMesh>
  );
}
